import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useHabits } from '@/hooks/useHabits';
import { useGamification } from '@/hooks/useGamification';
import { useCheckin } from '@/hooks/useCheckin';
import type { ChatSession, CoachingCategory } from '@/types';
import { SimpleXPBar } from '@/components/gamification';
import {
  CATEGORY_ICONS, CATEGORY_GRADIENTS,
  Sun, Target, CheckCircle, Flame, MessageCircle, BarChart3,
} from '@/lib/category-icons';

interface HomeDashboardProps {
  sessions: ChatSession[];
  onNavigateToChat: () => void;
  onNavigateToHabits: () => void;
  onStartNewChat: () => void;
  onSelectSession: (sessionId: string) => void;
}

const CATEGORIES: { id: CoachingCategory; label: string; color: string }[] = [
  { id: 'faith', label: 'Faith', color: CATEGORY_GRADIENTS.faith ?? 'from-amber-500 to-orange-500' },
  { id: 'family', label: 'Family', color: CATEGORY_GRADIENTS.family ?? 'from-rose-500 to-pink-500' },
  { id: 'finance', label: 'Finance', color: CATEGORY_GRADIENTS.finance ?? 'from-emerald-500 to-teal-500' },
  { id: 'fitness', label: 'Fitness', color: CATEGORY_GRADIENTS.fitness ?? 'from-blue-500 to-cyan-500' },
];

export function HomeDashboard({
  sessions,
  onNavigateToChat,
  onNavigateToHabits,
  onStartNewChat,
  onSelectSession,
}: HomeDashboardProps) {
  const { user } = useAuth();
  const { todayProgress } = useHabits();
  const { stats, levelProgress, categoryProgress } = useGamification();
  const { checkin, loading: checkinLoading } = useCheckin();

  const recentSessions = sessions.slice(0, 5);

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-strong rounded-2xl p-6"
        >
          <h1 className="text-2xl font-bold text-white mb-1">
            Welcome back{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
          </h1>
          <p className="text-slate-400">
            Balance your Faith, Family, Finance, and Fitness journey.
          </p>
        </motion.div>

        {/* Daily Check-in */}
        {!checkinLoading && checkin && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-strong rounded-2xl p-6 border-l-4 border-amber-500"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-royal-gradient flex items-center justify-center shadow-glow flex-shrink-0">
                <Sun className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
                  Daily Check-in
                  {checkin.alreadyCheckedIn && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                      Completed
                    </span>
                  )}
                </h2>
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                  {checkin.checkinMessage}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Today's Habits Card */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-strong rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                Today's Habits
              </h2>
              <button
                onClick={onNavigateToHabits}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
              >
                View All
              </button>
            </div>

            <div className="space-y-4">
              {/* Progress Circle */}
              <div className="flex items-center gap-6">
                <div className="relative w-24 h-24">
                  <svg className="w-24 h-24 -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-charcoal-700"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="url(#gradient)"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${todayProgress.percentage * 2.51} 251`}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#e74c3c" />
                        <stop offset="50%" stopColor="#f5b041" />
                        <stop offset="100%" stopColor="#5dade2" />
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {todayProgress.percentage}%
                    </span>
                  </div>
                </div>

                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-white">
                      {todayProgress.completed}/{todayProgress.total} completed
                    </span>
                  </div>
                  {stats && (
                    <>
                      <div className="flex items-center gap-2">
                        <Flame className="w-5 h-5 text-amber-400" />
                        <span className="text-white">
                          {stats.longestStreak} day streak
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-amber-400 font-semibold">
                          Level {stats.currentLevel}
                        </span>
                        <span className="text-slate-400">&middot;</span>
                        <span className="text-slate-400">
                          {stats.totalXp.toLocaleString()} XP
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Level Progress */}
              {stats && (
                <div className="pt-2">
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                    <span>Level {stats.currentLevel}</span>
                    <span>Level {stats.currentLevel + 1}</span>
                  </div>
                  <SimpleXPBar progress={levelProgress} size="md" />
                </div>
              )}
            </div>
          </motion.div>

          {/* Recent Conversations Card */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-strong rounded-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-amber-400" />
                Recent Conversations
              </h2>
              <button
                onClick={onNavigateToChat}
                className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
              >
                View All
              </button>
            </div>

            <div className="space-y-2">
              {recentSessions.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-slate-500 mb-3">No conversations yet</p>
                  <button
                    onClick={onStartNewChat}
                    className="px-4 py-2 rounded-lg bg-royal-gradient text-white text-sm font-medium
                             shadow-glow hover:opacity-90 transition-all"
                  >
                    Start Your First Chat
                  </button>
                </div>
              ) : (
                <>
                  {recentSessions.map((session) => {
                    const Icon = session.category ? CATEGORY_ICONS[session.category] : null;
                    return (
                      <button
                        key={session.id}
                        onClick={() => {
                          onSelectSession(session.id);
                          onNavigateToChat();
                        }}
                        className="w-full p-3 rounded-lg bg-charcoal-800/50 hover:bg-charcoal-800
                                 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-3">
                          {Icon && <Icon className="w-5 h-5 text-slate-400" />}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">
                              {session.title || 'New Conversation'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(session.updatedAt).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                          <span className="text-slate-600 group-hover:text-slate-400 transition-colors">
                            &rarr;
                          </span>
                        </div>
                      </button>
                    );
                  })}
                  <button
                    onClick={onStartNewChat}
                    className="w-full p-3 rounded-lg border border-dashed border-charcoal-600
                             hover:border-amber-500/50 hover:bg-charcoal-800/30 transition-all
                             text-slate-400 hover:text-white text-sm flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Start New Chat
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </div>

        {/* 4 F's Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-strong rounded-2xl p-6"
        >
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-400" />
            The 4 F's Balance
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CATEGORIES.map((category) => {
              const progress = categoryProgress[category.id];
              const Icon = CATEGORY_ICONS[category.id];
              return (
                <div
                  key={category.id}
                  className="p-4 rounded-xl bg-charcoal-800/50 border border-charcoal-700/50"
                >
                  <div className="flex items-center gap-2 mb-3">
                    {Icon && <Icon className="w-6 h-6 text-slate-300" />}
                    <span className="text-white font-medium">{category.label}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="h-2 bg-charcoal-900 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progress.percentage}%` }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        className={`h-full bg-gradient-to-r ${category.color}`}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">{progress.xp.toLocaleString()} XP</span>
                      <span className="text-slate-500">{progress.percentage}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Balance Insight */}
          {stats && (
            <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-sm text-amber-300">
                {getBalanceInsight(categoryProgress)}
              </p>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid sm:grid-cols-2 gap-4"
        >
          <button
            onClick={onNavigateToChat}
            className="p-6 rounded-2xl bg-gradient-to-br from-red-500/20 to-amber-500/20
                     border border-amber-500/30 hover:border-amber-500/50
                     transition-all group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-royal-gradient flex items-center justify-center shadow-glow">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white group-hover:text-amber-300 transition-colors">
                  AI Coaching
                </h3>
                <p className="text-sm text-slate-400">Get personalized guidance on your 4 F's journey</p>
              </div>
            </div>
          </button>

          <button
            onClick={onNavigateToHabits}
            className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20
                     border border-emerald-500/30 hover:border-emerald-500/50
                     transition-all group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white group-hover:text-emerald-300 transition-colors">
                  Track Habits
                </h3>
                <p className="text-sm text-slate-400">Build consistent routines across all 4 F's</p>
              </div>
            </div>
          </button>
        </motion.div>
      </div>
    </div>
  );
}

function getBalanceInsight(categoryProgress: {
  faith: { xp: number; percentage: number };
  family: { xp: number; percentage: number };
  finance: { xp: number; percentage: number };
  fitness: { xp: number; percentage: number };
}): string {
  type CategoryKey = 'faith' | 'family' | 'finance' | 'fitness';
  const categories: CategoryKey[] = ['faith', 'family', 'finance', 'fitness'];
  const sorted = [...categories].sort(
    (a, b) => categoryProgress[b].xp - categoryProgress[a].xp
  );

  const highest = sorted[0] as CategoryKey;
  const lowest = sorted[sorted.length - 1] as CategoryKey;

  if (categoryProgress[highest].xp === 0) {
    return "Start building habits in any category to see your balance insights!";
  }

  const lowestPercentage = categoryProgress[lowest].percentage;
  const highestLabel = highest.charAt(0).toUpperCase() + highest.slice(1);
  const lowestLabel = lowest.charAt(0).toUpperCase() + lowest.slice(1);

  if (lowestPercentage >= 70) {
    return `Great balance! You're making strong progress across all 4 F's.`;
  }

  if (lowestPercentage < 30) {
    return `Tip: Your ${lowestLabel} could use some attention. Consider adding habits in this area to achieve better balance.`;
  }

  return `You're strongest in ${highestLabel}. Keep building momentum in ${lowestLabel} to maintain balance.`;
}
